import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Users, Smile, TrendingUp, Trash2, Download, Eye } from 'lucide-react';
import { toast } from 'sonner';

const NPS_BADGE = {
  promoter: 'bg-green-500/15 text-green-600',
  passive: 'bg-amber-500/15 text-amber-600',
  detractor: 'bg-red-500/15 text-red-600',
};

const RENEWAL_LABELS = {
  definitely: 'Definitely',
  probably: 'Probably',
  unsure: 'Unsure',
  probably_not: 'Probably not',
  definitely_not: 'Definitely not',
};

const CSAT_LABELS = { 1: 'Very dissatisfied', 2: 'Dissatisfied', 3: 'Neutral', 4: 'Satisfied', 5: 'Very satisfied' };

const MATRIX_ROWS = [
  ['rating_cleanliness', 'Cleanliness'],
  ['rating_wifi', 'Wi-Fi'],
  ['rating_ac_lighting', 'AC & lighting'],
  ['rating_meeting_rooms', 'Meeting rooms'],
  ['rating_front_desk', 'Front desk team'],
  ['rating_maintenance', 'Maintenance'],
  ['rating_value', 'Value for money'],
];

const IMPROVEMENT_LABELS = {
  wifi: 'Wi-Fi', meeting_rooms: 'Meeting rooms', cleanliness: 'Cleanliness', f_and_b: 'F&B',
  events: 'Events', faster_resolution: 'Faster resolution', other: 'Other',
};

const ratingText = (v) => (v === 'na' ? 'N/A' : v == null ? '—' : `${v} / 5`);

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <div className="text-xs font-medium text-muted-foreground">{label}</div>
    <div className="text-sm whitespace-pre-wrap break-words">{children}</div>
  </div>
);

// Every answer of one response, in form order.
const ResponseDetail = ({ r }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Submitted">{new Date(r.submitted_at).toLocaleString()}</Field>
      <Field label="1. Work email">{r.email}</Field>
      <Field label="2. Centre">{r.feedback_centre_name}{r.feedback_centre_city ? ` (${r.feedback_centre_city})` : ''}</Field>
      <Field label="3. Overall satisfaction (CSAT)">{r.csat_score} / 5 — {CSAT_LABELS[r.csat_score]}</Field>
      <Field label="4. Likelihood to recommend (NPS)">
        {r.nps_score} / 10 <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium capitalize ${NPS_BADGE[r.nps_category] || ''}`}>{r.nps_category}</span>
      </Field>
    </div>
    <Field label="5. Main reason for the score">{r.feedback_reason}</Field>
    <div className="space-y-2">
      <div className="text-xs font-medium text-muted-foreground">6. Ratings</div>
      <div className="rounded-md border divide-y">
        {MATRIX_ROWS.map(([key, label]) => (
          <div key={key} className="flex justify-between px-3 py-1.5 text-sm">
            <span>{label}</span><span className="font-medium">{ratingText(r[key])}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="7. Ease of issue resolution (CES)">
        {r.ces_score === 'not_raised' ? "Haven't raised an issue" : ratingText(r.ces_score)}
      </Field>
      <Field label="9. Renewal intent">{RENEWAL_LABELS[r.renewal_intent] || '—'}</Field>
    </div>
    <Field label="8. What would most improve the experience">
      {r.improvement_areas?.length
        ? r.improvement_areas.map((a) => IMPROVEMENT_LABELS[a] || a).join(', ') + (r.improvement_other ? ` — "${r.improvement_other}"` : '')
        : '—'}
    </Field>
    <Field label="10. Permissions">
      <div>May contact about this feedback: <span className="font-medium">{r.feedback_contact_ok ? 'Yes' : 'No'}</span></div>
      <div>May use as testimonial: <span className="font-medium">{r.testimonial_ok ? 'Yes' : 'No'}</span></div>
    </Field>
  </div>
);

// Column order for the CSV export — uses the integration's property names.
const CSV_COLUMNS = [
  'submitted_at', 'email', 'feedback_centre_name', 'csat_score', 'nps_score', 'nps_category', 'feedback_reason',
  'rating_cleanliness', 'rating_wifi', 'rating_ac_lighting', 'rating_meeting_rooms', 'rating_front_desk',
  'rating_maintenance', 'rating_value', 'ces_score', 'improvement_areas', 'improvement_other',
  'renewal_intent', 'feedback_contact_ok', 'testimonial_ok',
];

const toCsv = (rows) => {
  const esc = (v) => {
    const s = Array.isArray(v) ? v.join('; ') : v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [CSV_COLUMNS.join(','), ...rows.map((r) => CSV_COLUMNS.map((c) => esc(r[c])).join(','))].join('\n');
};

export const MemberFeedbackTab = ({ api }) => {
  const [venues, setVenues] = useState([]);
  const [responses, setResponses] = useState([]);
  const [summary, setSummary] = useState({ total_responses: 0, avg_csat: 0, nps: null });
  const [loading, setLoading] = useState(true);
  const [centreFilter, setCentreFilter] = useState('all');
  const [viewing, setViewing] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = centreFilter !== 'all' ? { centre_id: centreFilter } : {};
      const [venuesRes, responsesRes, summaryRes] = await Promise.all([
        api.get('/venues', { params: { include_inactive: true } }),
        api.get('/member-feedback/responses', { params }),
        api.get('/member-feedback/summary', { params }).catch(() => ({ data: null })),
      ]);
      setVenues(venuesRes.data || []);
      setResponses(responsesRes.data.responses || []);
      if (summaryRes.data) setSummary(summaryRes.data);
    } catch (err) {
      toast.error('Failed to load member feedback');
    } finally {
      setLoading(false);
    }
  }, [api, centreFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const deleteResponse = async (response) => {
    if (!window.confirm(`Delete the feedback response from ${response.email}?`)) return;
    try {
      await api.delete(`/member-feedback/responses/${response.id}`);
      toast.success('Response deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete response');
    }
  };

  const exportCsv = () => {
    const blob = new Blob([toCsv(responses)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `clayworks-member-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading) {
    return <div className="py-12 text-center text-muted-foreground">Loading member feedback…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Users className="w-5 h-5 text-primary" />
          <div><div className="text-xs text-muted-foreground">Total Responses</div><div className="text-xl font-bold">{summary.total_responses}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Smile className="w-5 h-5 text-primary" />
          <div><div className="text-xs text-muted-foreground">Average CSAT (1–5)</div><div className="text-xl font-bold">{summary.avg_csat || '—'}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-primary" />
          <div>
            <div className="text-xs text-muted-foreground">NPS</div>
            <div className="text-xl font-bold">{summary.nps ?? '—'}</div>
            {summary.total_responses > 0 && (
              <div className="text-xs text-muted-foreground">{summary.promoters} promoters · {summary.passives} passives · {summary.detractors} detractors</div>
            )}
          </div>
        </CardContent></Card>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-lg font-semibold">Member Feedback</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={centreFilter} onValueChange={setCentreFilter}>
            <SelectTrigger className="w-56"><SelectValue placeholder="All Centres" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Centres</SelectItem>
              {venues.map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!responses.length} data-testid="mo-member-feedback-export">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Centre</TableHead>
                <TableHead>CSAT</TableHead>
                <TableHead>NPS</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Renewal</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {responses.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No member feedback yet</TableCell></TableRow>
              ) : responses.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setViewing(r)} data-testid={`mo-member-feedback-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">{new Date(r.submitted_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-sm">{r.email}</TableCell>
                  <TableCell className="font-medium">{r.feedback_centre_name}</TableCell>
                  <TableCell>{r.csat_score} / 5</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${NPS_BADGE[r.nps_category] || ''}`}>{r.nps_score}</span>
                  </TableCell>
                  <TableCell className="max-w-xs text-sm text-muted-foreground"><p className="line-clamp-2">{r.feedback_reason}</p></TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{RENEWAL_LABELS[r.renewal_intent] || '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {[r.feedback_contact_ok && 'Contact', r.testimonial_ok && 'Testimonial'].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setViewing(r); }} aria-label="View response" data-testid={`mo-member-feedback-view-${r.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deleteResponse(r); }} aria-label="Delete response">
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Member Feedback Response</DialogTitle></DialogHeader>
          {viewing && <ResponseDetail r={viewing} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
